/**
 * 統計詳細テーブル等のキー表示ラベル（mean1 → 「WTの平均」など）。
 * ResultTabs / CalculationDetails で共有。
 */

import { formatManuscriptFixedDigits } from '../../../../utils/keisukeManuscriptFormat';

const STAT_KEY_I18N: Record<string, string> = {
  t: 'keisuke.ttest.t',
  z: 'keisuke.ttest.z',
  df: 'keisuke.ttest.df',
  p: 'keisuke.ttest.pValue',
  pValue: 'keisuke.ttest.pValue',
  mean1: 'keisuke.ttest.mean1',
  mean2: 'keisuke.ttest.mean2',
  sd1: 'keisuke.dashboard.resultStats.sd1',
  sd2: 'keisuke.dashboard.resultStats.sd2',
  stdDev1: 'keisuke.dashboard.resultStats.sd1',
  stdDev2: 'keisuke.dashboard.resultStats.sd2',
  n1: 'keisuke.dashboard.resultStats.n1',
  n2: 'keisuke.dashboard.resultStats.n2',
  meanDiff: 'keisuke.dashboard.resultStats.meanDiff',
  ciLower: 'keisuke.dashboard.resultStats.ciLower',
  ciUpper: 'keisuke.dashboard.resultStats.ciUpper',
  '95%CI lower': 'keisuke.dashboard.resultStats.ciLower',
  '95%CI upper': 'keisuke.dashboard.resultStats.ciUpper',
  method: 'keisuke.dashboard.resultStats.method',
  'Levene F': 'keisuke.dashboard.resultStats.leveneF',
  'Levene p': 'keisuke.dashboard.resultStats.leveneP',
  'F-test (variance) F': 'keisuke.dashboard.resultStats.fTestVarianceF',
  'F-test (variance) p': 'keisuke.dashboard.resultStats.fTestVarianceP',
  /** 基本統計量テーブル */
  column: 'keisuke.dashboard.resultStats.column',
  n: 'keisuke.basicStatistics.n',
  mean: 'keisuke.basicStatistics.mean',
  median: 'keisuke.basicStatistics.median',
  stdDev: 'keisuke.basicStatistics.stdDev',
  stdError: 'keisuke.basicStatistics.stdError',
  min: 'keisuke.basicStatistics.min',
  max: 'keisuke.basicStatistics.max',
  q1: 'keisuke.basicStatistics.q1',
  q3: 'keisuke.basicStatistics.q3',
};

/** mean1/sd1/n1 → 群A側。実群名があれば「群1」表記を置き換える */
const STAT_KEY_GROUP_SIDE: Record<string, 'a' | 'b'> = {
  mean1: 'a',
  mean2: 'b',
  median1: 'a',
  median2: 'b',
  sd1: 'a',
  sd2: 'b',
  stdDev1: 'a',
  stdDev2: 'b',
  n1: 'a',
  n2: 'b',
};

/** adapter が埋める `Shapiro–Wilk W (WT)` / `Shapiro–Wilk p (diff)` */
const SHAPIRO_KEY_RE = /^Shapiro–Wilk ([Wp]) \((.+)\)$/;

export type KeisukeStatLabelT = (
  key: string,
  opts?: { defaultValue?: string; group?: string }
) => string;

/** mean1/sd1/n1 → 実群名付き。仮定検定キーは日本語（または UI 言語）へ */
export function formatStatKeyLabel(
  key: string,
  t: KeisukeStatLabelT,
  groups?: { a: string; b: string }
): string {
  const side = STAT_KEY_GROUP_SIDE[key];
  const groupName = side && groups ? groups[side] : undefined;
  if (groupName) {
    const kind = key.startsWith('mean')
      ? 'mean'
      : key.startsWith('median')
        ? 'median'
        : key.startsWith('n')
          ? 'n'
          : 'sd';
    const namedKey = `keisuke.dashboard.resultStats.${kind}Named` as const;
    const fallback =
      kind === 'mean'
        ? `${groupName} mean`
        : kind === 'median'
          ? `${groupName} median`
          : kind === 'n'
            ? `n (${groupName})`
            : `SD (${groupName})`;
    return t(namedKey, { group: groupName, defaultValue: fallback });
  }

  const shapiro = SHAPIRO_KEY_RE.exec(key);
  if (shapiro) {
    const kind = shapiro[1] === 'W' ? 'shapiroWNamed' : 'shapiroPNamed';
    const rawGroup = shapiro[2];
    const group =
      rawGroup === 'diff'
        ? t('keisuke.dashboard.resultStats.shapiroDiffGroup', { defaultValue: 'diff' })
        : rawGroup;
    return t(`keisuke.dashboard.resultStats.${kind}`, {
      group,
      defaultValue: key,
    });
  }

  const i18nKey = STAT_KEY_I18N[key];
  if (i18nKey) {
    return t(i18nKey, { defaultValue: key });
  }
  return key;
}

/** adapter が埋める仮定検定キー（正規性・等分散） */
export function isAssumptionStatKey(key: string): boolean {
  return (
    key.startsWith('Shapiro–Wilk') ||
    key.startsWith('Levene ') ||
    key.startsWith('F-test (variance)')
  );
}

/** 主結果と仮定検定に分けて表示順を保つ */
export function partitionStatEntries<T>(
  entries: Array<[string, T]>
): { core: Array<[string, T]>; assumptions: Array<[string, T]> } {
  const core: Array<[string, T]> = [];
  const assumptions: Array<[string, T]> = [];
  for (const entry of entries) {
    (isAssumptionStatKey(entry[0]) ? assumptions : core).push(entry);
  }
  return { core, assumptions };
}

/**
 * 群間比較ストリップ／平均 glance に既出の記述統計。
 * カード下プレビューでは t・df・p・平均差・CI など差分だけ見せる。
 */
const COMPARISON_GLANCE_STAT_KEYS = new Set([
  'method',
  'mean1',
  'mean2',
  'median1',
  'median2',
  'sd1',
  'sd2',
  'stdDev1',
  'stdDev2',
  'n1',
  'n2',
]);

/** 群間比較タブ内の要点表: 平均・SD・n・method など既出行を除く */
export function filterCoreStatsBeyondGlance<T>(
  coreEntries: Array<[string, T]>,
  limit = 12
): Array<[string, T]> {
  return coreEntries
    .filter(([key]) => !COMPARISON_GLANCE_STAT_KEYS.has(key))
    .slice(0, limit);
}

const SHAPIRO_P_KEY_RE = /^Shapiro–Wilk p \((.+)\)$/;

/**
 * 仮定検定の数値から、手法選択の短い案内文キーを返す。
 * 数値そのものは表に任せ、解釈の一歩だけ添える。
 */
export function assumptionHintMessageKey(
  assumptions: Array<[string, unknown]>,
  method: string | undefined
):
  | 'assumptionHintNonnormal'
  | 'assumptionHintNonnormalUsingNP'
  | 'assumptionHintPreferWelch'
  | 'assumptionHintUsingNonparametric'
  | 'assumptionHintOk' {
  const isNonparametric = method === 'mann-whitney' || method === 'wilcoxon';
  let minShapiroP = Number.POSITIVE_INFINITY;
  let leveneP: number | undefined;
  for (const [key, raw] of assumptions) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) continue;
    if (SHAPIRO_P_KEY_RE.test(key)) {
      minShapiroP = Math.min(minShapiroP, n);
    } else if (key === 'Levene p') {
      leveneP = n;
    }
  }
  if (Number.isFinite(minShapiroP) && minShapiroP < 0.05) {
    return isNonparametric
      ? 'assumptionHintNonnormalUsingNP'
      : 'assumptionHintNonnormal';
  }
  if (
    typeof leveneP === 'number' &&
    leveneP < 0.05 &&
    method === 'student'
  ) {
    return 'assumptionHintPreferWelch';
  }
  if (isNonparametric) {
    return 'assumptionHintUsingNonparametric';
  }
  return 'assumptionHintOk';
}

/** 計算過程サマリ表など: 「WTの平均 / KOの平均」 */
export function formatStatKeyPairLabel(
  keyA: string,
  keyB: string,
  t: KeisukeStatLabelT,
  groups?: { a: string; b: string }
): string {
  return `${formatStatKeyLabel(keyA, t, groups)} / ${formatStatKeyLabel(keyB, t, groups)}`;
}

export function formatMethodStatValue(
  method: string,
  t: KeisukeStatLabelT
): string {
  return t(`keisuke.dashboard.resultStats.methodValue.${method}`, {
    defaultValue: method,
  });
}

/** 計算過程・用量反応など共通の数値表示（極小値は指数表記） */
export function formatKeisukeNumber(value: number, digits = 4): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs !== 0 && abs < 10 ** -digits) return value.toExponential(3);
  return value.toFixed(digits).replace(/\.?0+$/, '');
}

/** 群間比較・統計表・ANOVA 過程で共有（p < 0.001 表記） */
export function formatPValueCell(p: number): string {
  if (p < 0.001) return '< 0.001';
  return p.toFixed(4);
}

/** 論文用と同じ半分切り上げの小数2桁（極小・極大は指数） */
function formatStatFixed2(value: number): string {
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-3)) return value.toExponential(3);
  return formatManuscriptFixedDigits(value, 2);
}

function isPValueStatKey(k: string): boolean {
  return (
    k === 'pvalue' ||
    k === 'p' ||
    k.endsWith('pvalue') ||
    k.includes('pval') ||
    /(^|\s)p(\s|$|\()/.test(k) ||
    /\bp\b/.test(k)
  );
}

/** 平均・SD・CI・t など、論文表記と揃える2桁キー */
function isFixed2StatKey(k: string): boolean {
  if (
    k === 'mean' ||
    k === 'mean1' ||
    k === 'mean2' ||
    k === 'meandiff' ||
    k === 'median' ||
    k === 'median1' ||
    k === 'median2' ||
    k === 't' ||
    k === 'z' ||
    k === 'sd' ||
    k === 'sd1' ||
    k === 'sd2' ||
    k === 'stddev' ||
    k === 'stddev1' ||
    k === 'stddev2' ||
    k === 'stderr' ||
    k === 'stderror' ||
    k === 'min' ||
    k === 'max' ||
    k === 'q1' ||
    k === 'q3' ||
    k === 'cilower' ||
    k === 'ciupper'
  ) {
    return true;
  }
  return k.includes('ci lower') || k.includes('ci upper') || /95%\s*ci/.test(k);
}

/** 統計詳細テーブル用: 長い小数・指数・method を読みやすくする */
export function formatStatValue(
  key: string,
  value: unknown,
  t?: KeisukeStatLabelT
): string {
  if (typeof value === 'string') {
    if (key === 'method' && t) return formatMethodStatValue(value, t);
    return value;
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return String(value ?? '');
  }
  const k = key.toLowerCase();
  if (isPValueStatKey(k)) return formatPValueCell(value);
  if (k === 'df' || k.endsWith('df') || k.includes('degree')) {
    return Number.isInteger(value) ? String(value) : formatManuscriptFixedDigits(value, 2);
  }
  if (k === 'n' || k === 'n1' || k === 'n2' || /^n\d+$/i.test(key)) {
    return Number.isInteger(value) ? String(value) : String(Math.round(value));
  }
  if (isFixed2StatKey(k)) return formatStatFixed2(value);
  if (Number.isInteger(value)) return String(value);
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-4)) {
    return value.toExponential(3);
  }
  return value.toFixed(4).replace(/\.?0+$/, '');
}
